import {
    ChangeDetectorRef, Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {FUIOptionsService, SelectionEvent} from "../../framework/options.service";
import {FormGroup} from "@angular/forms";
import {Subscription} from "rxjs";
import {FUISelectAction} from "../../select-action";
import {FUIEventsService} from "../../../events/events.service";

@Directive()
export abstract class FUIAbstractSelectInput extends FUIAbstractInput implements OnDestroy, OnInit {
    @Input() public field: string;
    @Input() public optionOpenable: boolean;
    @Input() public filterable: boolean;
    @Input() public clearSelectionOptionLabel: string;
    @Input() public allowClearSelectionOption: boolean = true;
    @Input() public allowDirectInput: boolean = false;
    @Input() public isNumeric: boolean = false;
    @Input() public total: number;
    @Input() public actions: FUISelectAction[];
    @Input() public optionElementHandler: (option: any, elementRef: ElementRef) => void;

    @Output() public filtered$: EventEmitter<string>;
    @Output() public select$: EventEmitter<any>;
    @Output() public optionOpened$: EventEmitter<any>;
    @Output() public action$: EventEmitter<FUISelectAction>;

    public filter: FormGroup;
    public filteredOptions: Array<any>;

    private _$click: Subscription;
    private _key: string;
    private _option: any;
    private _options: Array<any>;
    private _popped: boolean;
    private clicking = false;
    private _query: string;
    private _$valueChanges: Subscription;

    constructor(public elementRef: ElementRef,
                changeDetectorRef: ChangeDetectorRef,
                protected eventsService: FUIEventsService,
                protected optionsService: FUIOptionsService) {
        super(changeDetectorRef);
        this.added$ = new EventEmitter<boolean>();
        this.extended$ = new EventEmitter<boolean>();
        this.optionOpened$ = new EventEmitter<any>();
        this.action$ = new EventEmitter<FUISelectAction>();
        this.filterable = true;
        this.filtered$ = new EventEmitter<string>();
        this.select$ = new EventEmitter<any>();
        this.options = new Array<any>();
        this.filteredOptions = new Array<any>();
    }

    ngOnInit(): void {
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.clicking && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        )
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    get key(): string {
        return this._key;
    }

    @Input()
    set key(key: string) {
        if (this._key !== key) {
            this._key = key;

            if (this.value) {
                this.option = this.value;
            }
        }
    }

    @Input()
    set options(options: Array<any>) {
        if (options) {
            if (options.length != undefined) {
                this._options = options;
                this.filteredOptions = this._options;
            }
        }
    }

    get option(): any {
        return this._option;
    }

    set option(option: any) {
        if (option) {
            if (this.allowDirectInput && typeof option !== "object") {
                this._option = option;
            } else if (this.key) {
                this._option = option[this.key];
            }
        } else {
            this._option = option;
        }
    }

    /**
     * Only called on direct input of value
     * @param value
     */
    modelChanged(value: any): void {
        if (this.allowDirectInput) {
            super.value = value || null;
            this._option = value || null;
        }
    }

    get options(): Array<any> {
        return this._options;
    }

    get popped(): boolean {
        return this._popped;
    }

    set popped(popped: boolean) {
        this._popped = popped;
        if (popped) {
            this.focussed = true;
            this.focussed$.emit(this.focussed);
        }
    }

    get query(): string {
        return this._query;
    }

    set query(query: string) {
        if (this.query !== query) {
            this._query = query;

            if (this.filterable) {
                this.filteredOptions = new Array<any>();
                this.options.forEach((option) => {
                    if (option[this.key].toLowerCase().includes(this._query.toLowerCase())) {
                        this.filteredOptions.push(option);
                    }
                });
            } else {
                this.filtered$.emit(this._query);
                this.filteredOptions = this.options;
            }
        }
    }

    override get value(): any {
        return this._value;
    }

    @Input()
    override set value(value: any) {
        super.value = value;
        this.option = value;
    }

    public add(): void {
        this.added$.emit(true);
    }

    public override click(): void {
        if (!this.clicking) {
            this.clicking = true;
            setTimeout(() => this.clicking = false);
            this.focus();
            if (!this.allowDirectInput || this.popped) {
                this.popped = !this.popped;
            }
        }
    }

    public clickToggle(event: MouseEvent): void {
        if (this.allowDirectInput) {
            event.stopPropagation();
            if (!this.clicking) {
                this.clicking = true;
                setTimeout(() => this.clicking = false);
                this.focus();
                this.popped = !this.popped;
            }
        }
    }

    /**
     * Unless direct input is enabled, if a user presses enter or space while focussed on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled && !this.allowDirectInput) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public empty(options: Array<any>): boolean {
        return !this.loading && (!options || options.length === 0);
    }

    /**
     * Emit an event when the extended search button
     * is clicked
     */
    public extend(): void {
        this.extended$.emit(true);
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public filled(options: Array<any>): boolean {
        return options && options.length > 0;
    }

    /**
     * Check if any of the items required for the
     * floating tool bar are included to decide
     * whether or not to show it
     * @returns {boolean}
     */
    public ftb(): boolean {
        return (this.addable || this.extendable || this.actions || this.limited()) && !this.loading;
    }

    /**
     * Check if the returned options count
     * is less than the total number of items available.
     * @returns {boolean}
     */
    public limited(): boolean {
        return this.total && this.options && this.options.length < this.total;
    }

    /**
     * Checks if the list is filterable based on
     * it's length
     * @returns {boolean}
     */
    public searchable(): boolean {
        return this.options && (!this.filterable || this.options.length >= 10);
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Assign the selected value, and close the popup if the selection was made by the user
     * @param event
     */
    public select(event: SelectionEvent): void {
        this.value = event.selection;
        if (event.userSelection) {
            this.focus();
            this.popped = false;
            this.select$.emit(this.value);
        }
    }

    /**
     * Opens the currently selected option
     * @param {Event} event
     */
    openOption(event: Event) {
        event.stopPropagation();
        this.optionOpened$.next(this._value);
    }

    /**
     * Invoked when an optional action button is clicked
     */
    public actionClicked(action: FUISelectAction): void {
        if(!action.disabled){
            this.action$.emit(action);
        }
    }

    public override writeValue(value: any): void {
        this._value = value;

        if (this._value == undefined) {
            this.valued = false;
            this.option = undefined;
            this.optionsService.selection = undefined;
        } else {
            this.valued = true;
            this.option = value;
            this.optionsService.selection = this._value;
        }
        if (!this.destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }

    public onKeyDown(event: KeyboardEvent): void {
        if (this._value != undefined && typeof this._value === 'object') {
            event.target["value"] = this._value[this.field];
        }
        if (this.isNumeric && ["Backspace", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", "Tab", "0", "1", "2", "3", "4", "5", "6", "7", "8", "9"].indexOf(event.key) == -1) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    public onKeyUp(event: KeyboardEvent): void {
        if (this.isNumeric) {
            event.target["value"] = event.target["value"].replace(/\D*(\d+).*/, "$1");
        }
    }
}
